import { SortEnumType, TimeZoneFieldsFragment, useGetTimeZonesLazyQuery } from '@/common/infrastructure/gql/generated/schema';
import logger from '@/common/infrastructure/services/LoggerService';
import { useCallback, useEffect, useState } from 'react';
import { TypeaheadOption } from '../../../common/presentation/components/inputs/Typeahead';

// Type for timezone data from GraphQL
type TimeZoneData = TimeZoneFieldsFragment;

// Error state interface
interface TimeZoneError {
  message: string;
  code?: string;
  timestamp: Date;
}

// Helper function to safely map timezone data to TypeaheadOption
const mapTimezoneToOption = (tz: TimeZoneData): TypeaheadOption<string> | null => {
  const timeZoneName = tz?.timeZone ?? '';
  const shortName = tz?.shortName ?? '';
  const id = tz?.id ?? '';

  // Ensure we have a valid ID
  if (!id) {
    logger.warn('Skipping timezone with missing ID:', tz);
    return null;
  }

  // Create label with fallback chain
  // timeZoneName is the formatted display name like "(UTC+00:00) Dublin, Edinburgh, Lisbon, London"
  const label = timeZoneName || shortName || id;

  // Create displayText with proper null safety
  const displayText = shortName && timeZoneName ? `${timeZoneName} (${shortName})` : timeZoneName || shortName || undefined;

  // IMPORTANT: Use id (Windows timezone name) as value because:
  // 1. The backend expects Windows timezone name (e.g., "GMT Standard Time")
  // 2. The backend converts it to IANA using TimeZoneService.GetIANATimezone()
  // 3. timeZoneName is actually a formatted label, not an IANA identifier
  // Example: id = "GMT Standard Time", timeZoneName = "(UTC+00:00) Dublin, Edinburgh, Lisbon, London"
  const value = id;

  return {
    value,
    label,
    displayText,
    data: tz
  };
};

export function useTimeZoneTypeaheadFetch() {
  const [getTimeZones] = useGetTimeZonesLazyQuery({
    fetchPolicy: 'cache-first'
  });
  const [initialTimeZones, setInitialTimeZones] = useState<TypeaheadOption<string>[]>([]);
  const [error, setError] = useState<TimeZoneError | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);

  // Load initial timezones when the hook is mounted - only once
  useEffect(() => {
    if (hasLoadedInitial) return;

    const loadInitialTimeZones = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await getTimeZones({
          variables: {
            skip: 0,
            take: 200,
            order: [{ timeZone: SortEnumType.Asc }]
          }
        });

        const data = result?.data;
        const timezones =
          data?.timeZones?.items
            ?.map((tz) => {
              try {
                return mapTimezoneToOption(tz);
              } catch (error) {
                logger.warn('Skipping invalid timezone:', { timezone: tz, error });
                return null;
              }
            })
            .filter((option): option is TypeaheadOption<string> => option !== null) || [];

        setInitialTimeZones(timezones);
        setHasLoadedInitial(true);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load timezones';
        const timeZoneError: TimeZoneError = {
          message: errorMessage,
          code: 'TIMEZONE_LOAD_ERROR',
          timestamp: new Date()
        };

        logger.error('Error loading initial timezones', error);
        setError(timeZoneError);
        setHasLoadedInitial(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialTimeZones();
  }, [hasLoadedInitial, getTimeZones]);

  const fetchTimeZoneById = useCallback(
    async (timeZoneId: string): Promise<TypeaheadOption<string> | null> => {
      if (!timeZoneId) return null;

      // First, try to find in the already loaded initial timezones
      // Check multiple fields to support different formats:
      // - value: Windows timezone name (e.g., "GMT Standard Time") - this is what we now use as value
      // - id: Windows timezone name from data
      // - shortName: Windows timezone name (may be same as id)
      // - label: Formatted display name (e.g., "(UTC+00:00) Dublin, Edinburgh, Lisbon, London")
      // - timeZone: Formatted display name from data (legacy support)
      const foundInInitial = initialTimeZones.find(
        (tz) =>
          tz.value === timeZoneId ||
          tz.label === timeZoneId ||
          tz.displayText === timeZoneId ||
          (tz.data as TimeZoneData)?.id === timeZoneId ||
          (tz.data as TimeZoneData)?.shortName === timeZoneId ||
          (tz.data as TimeZoneData)?.timeZone === timeZoneId
      );

      if (foundInInitial) {
        logger.debug('Found timezone in initial data:', foundInInitial);
        return foundInInitial;
      }

      // If not found in initial data, try to fetch from backend
      try {
        logger.debug('Timezone not found in initial data, fetching from backend:', timeZoneId);

        // Try to find by timeZone name (contains)
        const result = await getTimeZones({
          variables: {
            skip: 0,
            take: 200,
            where: {
              or: [{ timeZone: { contains: timeZoneId } }, { shortName: { contains: timeZoneId } }]
            },
            order: [{ timeZone: SortEnumType.Asc }]
          }
        });

        const data = result?.data;
        // Search by id (Windows timezone name) first, then by other fields
        const timezone = data?.timeZones?.items?.find(
          (tz) =>
            tz.id === timeZoneId ||
            tz.shortName === timeZoneId ||
            tz.timeZone === timeZoneId ||
            tz.id?.includes(timeZoneId) ||
            tz.shortName?.includes(timeZoneId) ||
            tz.timeZone?.includes(timeZoneId)
        );

        if (!timezone) {
          logger.warn('Timezone not found for:', timeZoneId);
          return null;
        }

        try {
          const mappedTimezone = mapTimezoneToOption(timezone);
          logger.debug('Found timezone from backend:', mappedTimezone);
          return mappedTimezone;
        } catch (error) {
          logger.error('Error mapping timezone by ID', { timezone, error });
          return null;
        }
      } catch (error) {
        logger.error('Error fetching timezone by ID', error);
        return null;
      }
    },
    [getTimeZones, initialTimeZones]
  );

  const fetchTimeZonesBySearch = useCallback(
    async (searchTerm: string): Promise<TypeaheadOption<string>[]> => {
      try {
        const result = await getTimeZones({
          variables: {
            skip: 0,
            take: 200,
            where: searchTerm
              ? {
                  or: [{ timeZone: { contains: searchTerm } }, { shortName: { contains: searchTerm } }]
                }
              : undefined,
            order: [{ timeZone: SortEnumType.Asc }]
          }
        });

        const data = result?.data;
        return (
          data?.timeZones?.items
            ?.map((tz) => {
              try {
                return mapTimezoneToOption(tz);
              } catch (error) {
                logger.warn('Skipping invalid timezone:', { timezone: tz, error });
                return null;
              }
            })
            .filter((option): option is TypeaheadOption<string> => option !== null) || []
        );
      } catch (error) {
        logger.error('Error fetching timezones by search', error);
        return [];
      }
    },
    [getTimeZones]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    fetchTimeZoneById,
    fetchTimeZonesBySearch,
    initialTimeZones,
    error,
    isLoading,
    clearError
  };
}
